import clsx from 'clsx';
import React, {
  HTMLAttributes,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  Index,
  InfiniteLoader,
  List,
  WindowScroller,
} from 'react-virtualized';
import { queryProductFiltering } from '../../Actions/ProductFiltering.action';
import {
  addPlaceholderProducts,
  fetchProducts,
  setIsLoadingMore,
  setIsLoadingReverse,
} from '../../Actions/ProductListing.action';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import { ProductListingType } from '../../Reducers/ProductListing.reducer';
import { pushEcommerce } from '../../Services/Gtm.service';
import { translate } from '../../Services/translation';
import LoadMoreButton from '../LoadMoreButton/LoadMoreButton';
import ProductCard from '../ProductCards/ProductCard';
import ProductCardLandscape from '../ProductCards/ProductCardLandscape';
import LandscapeProductCardPlaceholder from '../ProductCards/ProductCardLandscapePlaceholder';
import ProductCardPlaceholder from '../ProductCards/ProductCardPlaceholder';
import ProductListingResizeObserver from './ProductListingResizeObserver';

const PRODUCTS_PER_LOAD = 60;
const DEFAULT_ROW_HEIGHT = 400;

const cache = new CellMeasurerCache({
  defaultWidth: 150,
  minWidth: 70,
  defaultHeight: 400, // TODO denna bör ändras beroende på vilken typ av vy som används på listan
  fixedWidth: true,
  fixedHeight: false,
});

const ProductListing = () => {
  const dispatch = useDispatch();

  /* Updates with delayToUpdate ms after resize.
    Prevents spamming of resize */
  useLayoutEffect(() => {
    var delay;
    function updateSize() {
      clearTimeout(delay);
      delay = setTimeout(() => {
        cache.clearAll();
        listRef?.recomputeRowHeights(0);
      }, 100);
    }

    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const {
    products,
    isLoadingMore,
    isLoadingReverse,
    totalProductsCount,
    productsPerRow = 5,
    canLoadMore,
    productListingType = ProductListingType.Grid,
    pageSize,
    gtmViewModel,
  } = useTypedSelector((state) => state.productListing);

  const [prevProducts, setPrevProducts] = useState(DEFAULT_ROW_HEIGHT);

  const [rowHeight, setRowHeight] = useState(400);

  const [ready, setReady] = useState(false);
  const rowCount = useMemo(
    () => Math.ceil(products.length / productsPerRow),
    [products.length, productsPerRow]
  );

  let listRef: List | null = null;

  const RowComponent = useCallback(
    ({ children, ...props }: HTMLAttributes<HTMLDivElement>) => {
      switch (productListingType) {
        case ProductListingType.Grid:
          setRowHeight(405);
          break;
        case ProductListingType.List:
          setRowHeight(150);
          break;
        case ProductListingType.SingleCard:
          setRowHeight(200);
          break;
        default:
          break;
    }

      return (
        <>
          {productListingType == ProductListingType.Grid && (
            <div
              {...props}
              className={clsx(
                props.className,
                'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 !gap-x-1 overflow-hidden'
              )}
            >
              {children}
            </div>
          )}
          {productListingType == ProductListingType.List && (
            <div
              {...props}
              className={clsx(props.className, 'grid grid-cols-1 !gap-0')}
            >
              {children}
            </div>
          )}
          {productListingType == ProductListingType.SingleCard && (
            <div
              {...props}
              className={clsx(props.className, 'grid grid-cols-1 !gap-0')}
            >
              {children}
            </div>
          )}
        </>
      );
    },
    [productListingType]
  );

  const [ProductComponent, ProductPlaceholderComponent] = useMemo(() => {
    switch (productListingType) {
      case ProductListingType.Grid:
      case ProductListingType.SingleCard:
      default:
        return [ProductCard, ProductCardPlaceholder];
      case ProductListingType.List:
        return [ProductCardLandscape, LandscapeProductCardPlaceholder];
    }
  }, [productListingType]);

  useEffect(() => {
    if (!ready) return;
    cache.clearAll();
    listRef?.recomputeRowHeights(0);
  }, [listRef, ready, productListingType, products, productsPerRow]);

  useEffect(() => {
    if (!ready) {
      pushEcommerce(gtmViewModel);
    }
    async function fetchProductList() {
      await dispatch(queryProductFiltering(null));
      setReady(true);
    }
    fetchProductList();
  }, []);

  const loadMoreClick = () => {
    const startIndex = rowCount;
    const endIndex =
      rowCount + Math.ceil(PRODUCTS_PER_LOAD / productsPerRow);
    dispatch(addPlaceholderProducts(startIndex, endIndex));
    dispatch(setIsLoadingMore(true));
  };

  const isRowLoaded = ({ index }: Index) => {
    if (isLoadingReverse) return true;
    return index >= rowCount || !!products[index * productsPerRow];
  };

  const loadMoreRows = ({ startIndex, stopIndex }): Promise<any> => {
    if (startIndex >= rowCount) {
      return new Promise((resolve) => resolve(null));
    }

    dispatch(setIsLoadingReverse(true));
    return dispatch(fetchProducts(startIndex, stopIndex))
      .then(() => {
        for (let i = startIndex; i < stopIndex; i++) {
          cache.clear(i, 0);
        }
        listRef?.recomputeRowHeights(0);
      })
      .finally(() => {
        dispatch(setIsLoadingReverse(false));
      });
  };

  const rowRenderer = useCallback(
    ({ index, key, parent, style }) => {
      const startIndex = index * productsPerRow;
      const endIndex = startIndex + productsPerRow;
      const productsForRow = products.slice(startIndex, endIndex);

      if (productListingType == ProductListingType.Grid) {
        return (
          <RowComponent key={key} style={style}>
            {productsForRow.length > 0 &&
              productsForRow.map((product, idx) =>
                product && ready ? (
                  <ProductComponent
                    product={product}
                    key={key + idx}
                    lazyLoadImage={true}
                    /* Maxheight is set to height - 4 to create a row-gap */
                    maxHeightPx={style['height'] - 4}
                  />
                ) : (
                  <ProductPlaceholderComponent key={key + idx} />
                )
              )}
          </RowComponent>
        );
      } else {
        return (
          <CellMeasurer
            cache={cache}
            rowIndex={index}
            columnIndex={0}
            parent={parent}
            key={key}
          >
            <RowComponent key={key} style={style}>
              {productsForRow.length > 0 &&
                productsForRow.map((product, idx) =>
                  product && ready ? (
                    <ProductComponent
                      product={product}
                      key={key + idx}
                      lazyLoadImage={true}
                    />
                  ) : (
                    <ProductPlaceholderComponent key={key + idx} />
                  )
                )}
            </RowComponent>
          </CellMeasurer>
        );
      }
    },
    [products, productsPerRow, ready]
  );

  return (
    <>
      {totalProductsCount > 0 ? (
        <div className="[&_.ReactVirtualized__Grid]:!overflow-visible [&_.ReactVirtualized__Grid__innerScrollContainer]:!overflow-visible">
          {!ready && (
            <RowComponent
              style={{
                gap:
                  productListingType == ProductListingType.Grid
                    ? '0.4rem !important'
                    : '0',
              }}
            >
              {products.map((product, idx) =>
                product && ready ? (
                  <ProductComponent
                    product={product}
                    key={'not-ready' + idx}
                    lazyLoadImage={false}
                  />
                ) : (
                  <ProductPlaceholderComponent key={'not-ready' + idx} />
                )
              )}
            </RowComponent>
          )}
          {ready && (
            <WindowScroller>
              {({ height, isScrolling, onChildScroll, scrollTop }) => {
                return (
                  <AutoSizer disableHeight>
                    {({ width }) => (
                      <InfiniteLoader
                        minimumBatchSize={pageSize / productsPerRow}
                        isRowLoaded={isRowLoaded}
                        loadMoreRows={loadMoreRows}
                        rowCount={rowCount}
                        threshold={5}
                      >
                        {({ onRowsRendered, registerChild }) => (
                          <List
                            autoHeight
                            ref={(ref) => {
                              if (ref) {
                                registerChild(ref);
                                if (
                                  productListingType ==
                                  ProductListingType.List
                                )
                                  listRef = ref;
                              }
                            }}
                            height={height}
                            onRowsRendered={onRowsRendered}
                            width={width}
                            isScrolling={isScrolling}
                            onScroll={onChildScroll}
                            scrollTop={scrollTop}
                            rowCount={rowCount}
                            rowRenderer={rowRenderer}
                            rowHeight={
                              productListingType == ProductListingType.Grid
                                ? rowHeight
                                : cache.rowHeight
                            }
                          />
                        )}
                      </InfiniteLoader>
                    )}
                  </AutoSizer>
                );
              }}
            </WindowScroller>
          )}
          <LoadMoreButton
            onClick={loadMoreClick}
            disabled={
              !canLoadMore ||
              isLoadingMore ||
              products.length >= totalProductsCount
            }
            isLoading={isLoadingMore}
            count={products.length}
            totalCount={totalProductsCount}
            productsPerLoad={PRODUCTS_PER_LOAD}
            canLoadMore={canLoadMore}
          />
          <ProductListingResizeObserver />
        </div>
      ) : (
        <div className="md-down:container">
          <h3>
            {translate('product-list.no-products-match-your-search')}
          </h3>
        </div>
      )}
    </>
  );
};

export default ProductListing;
